<template>
  <default-layout :breadcrumbs="breadcrumbs">
    <div class="recommended-page">
      <page-loading :show="loading" />

      <section class="recommended-hero">
        <div class="container">
          <div class="recommended-hero--copy">
            <div class="recommended-hero--badge">
              <v-icon>mdi-star-four-points-outline</v-icon>
              <span>Listing Rekomendasi</span>
            </div>

            <h1>Listing Anda Lebih Mudah Ditemukan</h1>

            <p>
              Tempatkan properti pilihan di area prioritas agar lebih mudah ditemukan calon pembeli
              dan penyewa.
            </p>

            <div class="recommended-hero--actions">
              <router-link
                class="btn recommended-hero--secondary"
                to="/layanan-agen/rekomendasi-listing/cara-kerja"
              >
                Pelajari Caranya
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <main class="recommended-content">
        <div class="container">
          <recommended-listing-list @actionDone="loadPage"></recommended-listing-list>
        </div>
      </main>
    </div>
  </default-layout>
</template>

<script>
import { mapState } from 'vuex';

import DefaultLayout from '@/layouts/default';
import DefaultHeadMixin from '@/mixins/defaultHead';
import RecommendedListingList from '@/components/recommended-listing/list';
import PageLoading from '@/components/content-loading/page-loading.vue';

export default {
  name: 'RecommendedListing',

  components: {
    DefaultLayout,
    PageLoading,
    RecommendedListingList,
  },

  mixins: [DefaultHeadMixin],

  computed: {
    ...mapState({
      loading: (state) => state.recommendedListing.loading,
      loggedIn: (state) => state.global.loggedIn,
      recommendedListings: (state) => state.recommendedListing.recommendedListings,
    }),

    breadcrumbs() {
      const currentName = this.$t('home.sidebarMenu.recommendedListing');

      return [
        {
          text: this.$t('breadcrumb.homepage'),
          to: '/',
        },
        {
          text: currentName,
          to: this.$router.currentRoute.path,
        },
      ];
    },
  },

  watch: {
    loggedIn(value) {
      if (value && (!this.recommendedListings || this.recommendedListings.length === 0)) {
        this.loadPage();
      }
    },
  },

  mounted() {
    if (this.loggedIn && (!this.recommendedListings || this.recommendedListings.length === 0)) {
      this.loadPage();
    }
  },

  methods: {
    async loadPage() {
      if (!this.loggedIn) {
        return;
      }

      await this.$store.dispatch('recommendedListing/getRecommendedListings', {
        page: 1,
      });
    },
  },

  async fetch({ store, route }) {
    await store.dispatch('recommendedListing/restoreInitialState');

    if (store.state.global.loggedIn) {
      await store.dispatch('recommendedListing/getRecommendedListings', {
        page: 1,
      });
    }

    store.commit('head/RESTORE_DEFAULT_VALUE', route);
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/utils/variables';

.recommended-page {
  background: #ffffff;
  color: $color-text-black;
}

.recommended-hero {
  padding: 20px 0 0;
}

.recommended-hero .container {
  display: flex;
  min-height: 240px;
  align-items: center;
  overflow: hidden;
  padding: 30px 34px;
  border-radius: 20px;
  color: #ffffff;
  background-image: linear-gradient(
      90deg,
      rgba(70, 7, 11, 0.92) 0%,
      rgba(169, 16, 24, 0.76) 48%,
      rgba(169, 16, 24, 0.16) 100%
    ),
    image-set(
      url('https://beliruma.s3.ap-southeast-1.amazonaws.com/public/static/img/static/v2/recommended-listing/header-listing-rekomendasi.webp')
        type('image/webp'),
      url('https://beliruma.s3.ap-southeast-1.amazonaws.com/public/static/img/static/v2/recommended-listing/header-listing-rekomendasi.png')
        type('image/png')
    );
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  box-shadow: 0 16px 36px rgba(57, 24, 27, 0.12);
}

.recommended-hero--copy {
  width: min(560px, 100%);
}

.recommended-hero--badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.14);
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  backdrop-filter: blur(8px);
}

.recommended-hero--badge .v-icon {
  color: inherit;
  font-size: 15px;
}

.recommended-hero h1 {
  max-width: 16ch;
  margin: 12px 0 8px;
  color: #ffffff;
  font-family: Poppins-Bold, sans-serif;
  font-size: 2.15rem;
  line-height: 1.18;
}

.recommended-hero p {
  max-width: 520px;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.55;
}

.recommended-hero--actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.recommended-hero--secondary {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border-radius: 8px;
  font-family: Poppins-SemiBold, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  white-space: nowrap;
  transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

.recommended-hero--secondary {
  border: 1px solid rgba(255, 255, 255, 0.36);
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
}

.recommended-hero--secondary:hover {
  transform: translateY(-1px);
  text-decoration: none;
}

.recommended-hero--secondary:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.18);
}

.recommended-hero--secondary:active {
  transform: translateY(0);
}

.recommended-content {
  padding: 34px 0 64px;
}

@media (max-width: 767.98px) {
  .recommended-hero {
    padding: 14px 16px 0;
  }

  .recommended-hero .container {
    min-height: 230px;
    padding: 24px 18px;
    border-radius: 16px;
    background-position: center right 32%;
  }

  .recommended-hero h1 {
    max-width: 14ch;
    font-size: 1.55rem;
  }

  .recommended-hero p {
    font-size: 12px;
    line-height: 1.5;
  }

  .recommended-hero--actions {
    width: 100%;
  }

  .recommended-hero--secondary {
    width: 100%;
    white-space: normal;
  }

  .recommended-content {
    padding: 24px 0 48px;
  }
}
</style>
